import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ResultsContext } from '@webapp/account/src/pages/survey/results/context';
import { Actions, getPeriodLabel, getPeriodName } from '@webapp/account/src/resources/reports';
import { selectUserChartsSettings } from '@webapp/account/src/resources/settings/selectors';

import { BoldCross } from '@webapp/common/assets/icons';
import { getPeriodColors, SCHEMES } from '@webapp/common/components/charts/utils';
import { useAction } from '@webapp/common/hooks/use-actions';

import css from './compare-periods-chips.css';

export const ComparePeriodsChips: FC<{ onEdit: () => void }> = ({ onEdit }) => {
    const { globalFilters } = useContext(ResultsContext);
    const patchFilters = useAction(Actions.patchFilters);
    const userChartsSettings = useSelector(selectUserChartsSettings);
    const chartColorSchemes = userChartsSettings.chartColorSchemes || SCHEMES;

    const periods = useMemo(() => globalFilters.comparePeriods || [], [globalFilters.comparePeriods]);
    const colors = getPeriodColors(periods.length + 1, chartColorSchemes);

    const removePeriod = useCallback(
        (id: string) => {
            patchFilters({ comparePeriods: periods.filter((period) => period.id !== id) });
        },
        [patchFilters, periods],
    );

    return (
        <div className={css.chips}>
            <div className={css.chip} onClick={onEdit}>
                <span className={css.dot} style={{ backgroundColor: colors[0] }} />
                <div className={css.chipText}>
                    <span className={css.chipRange}>{getPeriodLabel(globalFilters)}</span>
                    <span className={css.chipName}>{getPeriodName({ name: globalFilters.basePeriodName }, 0)}</span>
                </div>
            </div>
            {periods.map((period, index) => (
                <div className={css.chip} key={period.id} onClick={onEdit}>
                    <span className={css.dot} style={{ backgroundColor: colors[index + 1] }} />
                    <div className={css.chipText}>
                        <span className={css.chipRange}>{getPeriodLabel(period)}</span>
                        <span className={css.chipName}>{getPeriodName(period, index + 1)}</span>
                    </div>
                    <BoldCross
                        className={css.chipRemove}
                        onClick={(e) => {
                            e.stopPropagation();
                            removePeriod(period.id);
                        }}
                    />
                </div>
            ))}
        </div>
    );
};
